import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, FileText, Eye, Trash2, ExternalLink, Send, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PendingSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/stories/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubmissions(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (story) => {
    setSelectedStory(story);
    // Initialize with existing values or defaults
    setStoryTitle(story.title || story.startup_name || '');
    setShortDescription(story.short_description || story.brief_info?.substring(0, 150) || '');
    setViewOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/stories/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      fetchSubmissions();
    } catch (err) {
      toast.error('Failed to delete submission');
    }
  };

  const handlePublish = async () => {
    if (!storyTitle.trim()) {
      toast.error('Please add a title for the story');
      return;
    }
    if (!shortDescription.trim()) {
      toast.error('Please add a short description for the story');
      return;
    }

    setPublishing(true);
    try {
      const token = Cookies.get('admin_token');
      // TODO: Update story with title and description, then publish
      await axios.put(`${API}/stories/${selectedStory.id}`, {
        title: storyTitle,
        short_description: shortDescription,
        status: 'Published'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Story published successfully!');
      setViewOpen(false);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish story');
    } finally {
      setPublishing(false);
    }
  };

  const handleReactivateLink = async (id, startupName) => {
    if (!window.confirm(`Are you sure you want to reactivate the submission link for "${startupName}"?\n\nThis will delete the current submission and allow the founder to resubmit the story.`)) {
      return;
    }

    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/stories/${id}/reactivate-link`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Submission link reactivated successfully! The form can now be filled again.');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate link');
    }
  };

  const isLinkExpired = (submission) => {
    // Link is expired if the story is submitted (is_draft: false)
    return submission.is_draft === false;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Pending Submissions</h1>
          <p className="text-muted-foreground">View and manage story submissions from founders</p>
        </div>
      </div>

      {submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">No pending submissions yet</p>
              <p className="text-sm">Story submissions will appear here once founders complete the form</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {submissions.map((submission) => (
            <Card key={submission.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{submission.startup_name}</CardTitle>
                    <CardDescription>
                      <div className="flex items-center gap-4 text-sm">
                        <span>Submitted by: {submission.founder_email}</span>
                        <span>•</span>
                        <span>{formatDate(submission.submitted_at)}</span>
                      </div>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-purple-500/10 text-purple-700 border-purple-500/20">
                      {submission.status}
                    </Badge>
                    <Badge variant="outline" className={
                      submission.submission_type === 'Expert Written' 
                        ? 'bg-orange-500/10 text-orange-700 border-orange-500/20'
                        : submission.submission_type === 'Self Written'
                        ? 'bg-blue-500/10 text-blue-700 border-blue-500/20'
                        : 'bg-green-500/10 text-green-700 border-green-500/20'
                    }>
                      {submission.submission_type || 'Questionnaire'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Preview of answers */}
                  <div className="text-sm">
                    <p className="font-medium text-muted-foreground mb-1">Brief Info:</p>
                    <p className="line-clamp-2">{submission.brief_info}</p>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleView(submission)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Full Story
                    </Button>
                    
                    {/* Open Form button - disabled if link expired */}
                    <div className="relative group">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/submit-story/${submission.story_token}`, '_blank')}
                        disabled={isLinkExpired(submission)}
                        className={isLinkExpired(submission) ? 'cursor-not-allowed opacity-50' : ''}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Form
                      </Button>
                      {isLinkExpired(submission) && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          Link Expired - Story Already Submitted
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>

                    {/* Reactivate Link button - only show if link is expired */}
                    {isLinkExpired(submission) && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleReactivateLink(submission.id, submission.startup_name)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Reactivate Link
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(submission.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Full Story Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Story Preview & Publishing</DialogTitle>
          </DialogHeader>
          {selectedStory && (
            <div className="space-y-6 py-4">
              {/* Title and Description Editing Section */}
              <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg space-y-4">
                <h3 className="font-semibold text-blue-900">Publishing Information</h3>
                <div className="space-y-2">
                  <Label htmlFor="story-title">Story Title *</Label>
                  <Input
                    id="story-title"
                    value={storyTitle}
                    onChange={(e) => setStoryTitle(e.target.value)}
                    placeholder="Enter a catchy title for the story"
                    className="text-lg font-semibold"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="short-desc">Short Description *</Label>
                  <Textarea
                    id="short-desc"
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    placeholder="Write a brief description (2-3 sentences) that will appear in story listings"
                    rows={3}
                  />
                </div>
              </div>

              {/* Story Preview Section */}
              <div className="border rounded-lg p-6 space-y-6 bg-white">
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-2">{storyTitle || selectedStory.startup_name}</h2>
                  <p className="text-muted-foreground">{shortDescription || 'No description added'}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Submitted on {formatDate(selectedStory.submitted_at)}
                  </p>
                </div>

                {/* Banner Photo */}
                {selectedStory.banner_photo && (
                  <div className="my-6">
                    <img 
                      src={selectedStory.banner_photo} 
                      alt="Startup banner" 
                      className="w-full h-80 object-cover rounded-lg"
                    />
                  </div>
                )}

                {/* Founder Profile Pictures - Square and Centered */}
                {selectedStory.founder_profiles && selectedStory.founder_profiles.length > 0 && (
                  <div className="my-8">
                    <h4 className="font-semibold mb-4 text-center">Founded By</h4>
                    <div className={`grid gap-8 justify-items-center`}
                      style={{ gridTemplateColumns: `repeat(${selectedStory.founder_profiles.length}, 1fr)` }}
                    >
                      {selectedStory.founder_profiles.map((profile, index) => (
                        <div key={index} className="text-center flex flex-col items-center">
                          {profile.profile_picture && (
                            <>
                              <img 
                                src={profile.profile_picture} 
                                alt="Founder"
                                className="w-48 h-48 object-cover rounded-lg border-4 border-primary shadow-lg"
                                style={{ maxWidth: '200px', maxHeight: '200px' }}
                              />
                              <p className="font-semibold mt-3 text-lg">
                                {profile.founder_name || 'Founder Name'}
                              </p>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Submission Type Badge */}
              <div className="p-4 bg-accent/50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Submission Type:</span>
                  <Badge variant="outline" className={
                    selectedStory.submission_type === 'Expert Written' 
                      ? 'bg-orange-500/10 text-orange-700 border-orange-500/20'
                      : selectedStory.submission_type === 'Self Written'
                      ? 'bg-blue-500/10 text-blue-700 border-blue-500/20'
                      : 'bg-green-500/10 text-green-700 border-green-500/20'
                  }>
                    {selectedStory.submission_type || 'Questionnaire'}
                  </Badge>
                </div>
              </div>

              {/* Self Written Story Content */}
              {selectedStory.submission_type === 'Self Written' && selectedStory.article_content && (
                <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <h4 className="font-semibold mb-3 text-blue-900">Self-Written Story</h4>
                  <div 
                    className="prose prose-sm max-w-none text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: selectedStory.article_content }}
                  />
                </div>
              )}

              {/* Show questions only for Questionnaire type */}
              {(!selectedStory.submission_type || selectedStory.submission_type === 'Questionnaire') && (
                <>
                  {/* Question 1 */}
              <div>
                <h4 className="font-semibold mb-2">1. Give a brief info about your startup?</h4>
                <p className="text-muted-foreground">{selectedStory.brief_info || 'No answer provided'}</p>
              </div>

              {/* Question 2 */}
              <div>
                <h4 className="font-semibold mb-2">2. What made you start your journey and what problem does it solve?</h4>
                <p className="text-muted-foreground">{selectedStory.startup_journey || 'No answer provided'}</p>
              </div>

              {/* Question 3 */}
              <div>
                <h4 className="font-semibold mb-2">3. Tell us about yourself, your previous jobs/ventures?</h4>
                <p className="text-muted-foreground">{selectedStory.founder_background || 'No answer provided'}</p>
              </div>

              {/* Question 4 */}
              <div>
                <h4 className="font-semibold mb-2">4. Where is your startup based out of? Why do you think that is the best place for you?</h4>
                <p className="text-muted-foreground">{selectedStory.location_reason || 'No answer provided'}</p>
              </div>

              {/* Question 5 */}
              <div>
                <h4 className="font-semibold mb-2">5. As a startup founder, what are you paranoid about?</h4>
                <p className="text-muted-foreground">{selectedStory.founder_paranoia || 'No answer provided'}</p>
              </div>

              {/* Question 6 */}
              <div>
                <h4 className="font-semibold mb-2">6. Who are your competitions and how are you better than them?</h4>
                <p className="text-muted-foreground">{selectedStory.competition_edge || 'No answer provided'}</p>
              </div>

              {/* Question 7 */}
              <div>
                <h4 className="font-semibold mb-2">7. How hard is it to have a work-life balance as a startup founder?</h4>
                <p className="text-muted-foreground">{selectedStory.work_life_balance || 'No answer provided'}</p>
              </div>

              {/* Question 8 */}
              <div>
                <h4 className="font-semibold mb-2">8. Have you raised funding?</h4>
                <p className="text-muted-foreground">{selectedStory.funding_details || 'No answer provided'}</p>
              </div>

              {/* Question 9 */}
              <div>
                <h4 className="font-semibold mb-2">9. What gets you excited about this company?</h4>
                <p className="text-muted-foreground">{selectedStory.company_excitement || 'No answer provided'}</p>
              </div>

              {/* Question 10 */}
              <div>
                <h4 className="font-semibold mb-2">10. Tell us how a day in your life looks like?</h4>
                <p className="text-muted-foreground">{selectedStory.daily_schedule || 'No answer provided'}</p>
              </div>

              {/* Question 11 */}
              <div>
                <h4 className="font-semibold mb-2">11. Tell us about your team and how did you meet each other?</h4>
                <p className="text-muted-foreground">{selectedStory.team_info || 'No answer provided'}</p>
              </div>
                </>
              )}
              </div>

              {/* Publish Button */}
              <div className="flex gap-3 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setViewOpen(false)}
                  disabled={publishing}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handlePublish}
                  disabled={publishing || !storyTitle.trim() || !shortDescription.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {publishing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Publish Story</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
